import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { API_BASE_URL } from '../lib/api';

interface Account {
  id: string;
  accountName: string;
}

interface DealFormData {
  dealId: string;
  dealName: string;
  accountId: string;
  dealType: string;
  dealStatus: string;
  dealAmount: string;
  dealCost: string;
  dealProfit: string;
  dealOrderPlannedDate: string;
  dealOrderActualDate: string;
  dealStartDate: string;
  dealEndDate: string;
  remarks: string;
}

const initialFormData: DealFormData = {
  dealId: '',
  dealName: '',
  accountId: '',
  dealType: '',
  dealStatus: '見込み',
  dealAmount: '',
  dealCost: '',
  dealProfit: '',
  dealOrderPlannedDate: '',
  dealOrderActualDate: '',
  dealStartDate: '',
  dealEndDate: '',
  remarks: '',
};

const dealStatusOptions = ['見込み', '提案中', '交渉中', '受注', '失注', '保留'];
const dealTypeOptions = ['新規', '更新', 'アップセル', 'クロスセル', 'その他'];

export function DealForm() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [formData, setFormData] = useState<DealFormData>(initialFormData);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAccounts();
    if (isEdit) {
      fetchDeal();
    } else {
      const accountId = searchParams.get('accountId');
      const newId = `DEAL-${Date.now().toString(36).toUpperCase()}`;
      setFormData(prev => ({ 
        ...prev, 
        dealId: newId,
        accountId: accountId || '',
      }));
      setIsLoading(false);
    }
  }, [id]);

  const fetchAccounts = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/accounts?limit=1000`, {
        credentials: 'include',
      });
      const data = await response.json();
      setAccounts(data.data || []);
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchDeal = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/deals/${id}`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('案件情報の取得に失敗しました');
      }
      
      const data = await response.json();
      const deal = data.deal;
      
      setFormData({
        dealId: deal.dealId || '',
        dealName: deal.dealName || '',
        accountId: deal.accountId || '',
        dealType: deal.dealType || '',
        dealStatus: deal.dealStatus || '見込み',
        dealAmount: deal.dealAmount?.toString() || '',
        dealCost: deal.dealCost?.toString() || '',
        dealProfit: deal.dealProfit?.toString() || '',
        dealOrderPlannedDate: deal.dealOrderPlannedDate?.split('T')[0] || '',
        dealOrderActualDate: deal.dealOrderActualDate?.split('T')[0] || '',
        dealStartDate: deal.dealStartDate?.split('T')[0] || '',
        dealEndDate: deal.dealEndDate?.split('T')[0] || '',
        remarks: deal.remarks || '',
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // 売上と原価から利益を自動計算
      if (name === 'dealAmount' || name === 'dealCost') {
        const amount = parseFloat(name === 'dealAmount' ? value : prev.dealAmount) || 0;
        const cost = parseFloat(name === 'dealCost' ? value : prev.dealCost) || 0;
        updated.dealProfit = (amount - cost).toString();
      }
      
      return updated;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const url = isEdit 
        ? `${API_BASE_URL}/api/deals/${id}`
        : `${API_BASE_URL}/api/deals`;
      
      const method = isEdit ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          ...formData,
          dealAmount: formData.dealAmount ? parseFloat(formData.dealAmount) : null,
          dealCost: formData.dealCost ? parseFloat(formData.dealCost) : null,
          dealProfit: formData.dealProfit ? parseFloat(formData.dealProfit) : null,
          dealOrderPlannedDate: formData.dealOrderPlannedDate || null,
          dealOrderActualDate: formData.dealOrderActualDate || null,
          dealStartDate: formData.dealStartDate || null,
          dealEndDate: formData.dealEndDate || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || '保存に失敗しました');
      }

      navigate(`/deals/${data.id || id}`);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          style={{ color: 'var(--text-secondary)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          {isEdit ? '案件を編集' : '案件を新規作成'}
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardContent className="space-y-6 pt-6">
            {error && (
              <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* 基本情報 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="案件ID"
                name="dealId"
                value={formData.dealId}
                onChange={handleChange}
                required
                disabled={isEdit}
              />
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  ステータス
                </label>
                <select
                  name="dealStatus"
                  value={formData.dealStatus}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  {dealStatusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>
            </div>

            <Input
              label="案件名 *"
              name="dealName"
              value={formData.dealName}
              onChange={handleChange}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  企業 *
                </label>
                <select
                  name="accountId"
                  value={formData.accountId}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>{account.accountName}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                  案件種別
                </label>
                <select
                  name="dealType"
                  value={formData.dealType}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  style={{
                    backgroundColor: 'var(--background-card)',
                    borderColor: 'var(--border-color)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">選択してください</option>
                  {dealTypeOptions.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            <hr style={{ borderColor: 'var(--border-color)' }} />

            {/* 金額 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="売上金額"
                name="dealAmount"
                type="number"
                value={formData.dealAmount}
                onChange={handleChange}
                placeholder="0"
              />
              <Input
                label="原価"
                name="dealCost"
                type="number"
                value={formData.dealCost}
                onChange={handleChange}
                placeholder="0"
              />
              <Input
                label="利益（自動計算）"
                name="dealProfit"
                type="number"
                value={formData.dealProfit}
                onChange={handleChange}
                disabled
              />
            </div>

            <hr style={{ borderColor: 'var(--border-color)' }} />

            {/* 日付 */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="受注予定日"
                name="dealOrderPlannedDate"
                type="date"
                value={formData.dealOrderPlannedDate}
                onChange={handleChange}
              />
              <Input
                label="受注日"
                name="dealOrderActualDate"
                type="date"
                value={formData.dealOrderActualDate}
                onChange={handleChange}
              />
              <Input
                label="開始日"
                name="dealStartDate"
                type="date"
                value={formData.dealStartDate}
                onChange={handleChange}
              />
              <Input
                label="終了日"
                name="dealEndDate"
                type="date"
                value={formData.dealEndDate}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>
                備考
              </label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border rounded-lg resize-none"
                style={{
                  backgroundColor: 'var(--background-card)',
                  borderColor: 'var(--border-color)',
                  color: 'var(--text-primary)',
                }}
              />
            </div>

            {/* ボタン */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
              >
                キャンセル
              </Button>
              <Button
                type="submit"
                leftIcon={<Save size={16} />}
                isLoading={isSaving}
              >
                {isEdit ? '更新' : '作成'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
